// store/financialMigration.ts
// Forward-migration of persisted payloads. Every place an older localStorage or
// server document is read (buildInitialState, the fetch-fulfilled merge) routes
// through these so the shape the reducers see is always current.

import {
	DEFAULT_ASSUMPTIONS,
	DEFAULT_STATE,
	SCHEMA_VERSION,
	STORAGE_KEY,
} from './financialDefaults';
import type {
	AvgPriceSource,
	ExpenseEntry,
	ExpenseFrequency,
	FinancialState,
	IncomeCategory,
	IncomeEntry,
	IncomeFrequency,
	StockHolding,
} from './financialTypes';

export function loadFromStorage(
	key: string = STORAGE_KEY,
): Partial<FinancialState> | null {
	try {
		const raw = localStorage.getItem(key);
		return raw ? JSON.parse(raw) : null;
	} catch {
		return null;
	}
}

// Pre-V4 expense rows had only { id, name, amount, frequency }. Migrate them into
// 'other' with includeInMonthlySpending=true and flexibility UNSET so the UI can
// show an honest "needs review" prompt rather than guessing. Frequencies overlap,
// so amounts never shift. V12 phase/loan fields ride through untouched.
export function migrateExpenseEntry(
	raw: Partial<ExpenseEntry> & { id: string },
): ExpenseEntry {
	return {
		id: raw.id,
		name: raw.name ?? '',
		amount: raw.amount ?? '',
		frequency: (raw.frequency as ExpenseFrequency) ?? 'monthly',
		group: raw.group ?? 'other',
		flexibility: raw.flexibility,
		spreadMonths: raw.spreadMonths,
		includeInMonthlySpending: raw.includeInMonthlySpending ?? true,
		endYear: raw.endYear,
		loanBalance: raw.loanBalance,
		loanRatePercent: raw.loanRatePercent,
		loanPaymentMonthly: raw.loanPaymentMonthly,
		linkedAssetId: raw.linkedAssetId,
		notes: raw.notes,
	};
}

// Income redesign consolidated the category set from six values to three:
// side_income / freelance / business fold into self_employment, the dropped
// one_time folds into other, job_paycheck becomes job_income.
const LEGACY_INCOME_CATEGORY: Record<string, IncomeCategory> = {
	job_paycheck: 'job_income',
	job_income: 'job_income',
	side_income: 'self_employment',
	freelance: 'self_employment',
	business: 'self_employment',
	self_employment: 'self_employment',
	one_time: 'other',
	other: 'other',
};

export function migrateIncomeEntry(
	raw: Partial<IncomeEntry> & { id: string },
): IncomeEntry {
	const rawCat = raw.category as string | undefined;
	const category =
		rawCat !== undefined
			? (LEGACY_INCOME_CATEGORY[rawCat] ?? 'other')
			: undefined;
	return {
		id: raw.id,
		name: raw.name ?? '',
		amount: raw.amount ?? '',
		frequency: (raw.frequency as IncomeFrequency) ?? 'monthly',
		spreadMonths: raw.spreadMonths,
		category,
		incomeMode: raw.incomeMode,
		taxRate: raw.taxRate,
		notes: raw.notes,
	};
}

// V13 valuation fix — classify the provenance of a pre-fix average price.
// The old refresh path copied the market quote into avgPrice when it was
// empty, so an EXACT string match against the stored quote is that seeding.
// Any other pre-fix value is of unverifiable origin ('unknown'). Neither
// supports gain display until the user confirms what they paid. An empty
// avgPrice classifies as 'user': there is nothing to distrust, and the next
// write is the user's (updateHolding stamps 'user' on every avgPrice edit).
export function migrateAvgPriceSource(
	h: Partial<StockHolding>,
): AvgPriceSource {
	if (h.avgPriceSource) return h.avgPriceSource;
	const avg = (h.avgPrice ?? '').trim();
	if (!avg) return 'user';
	const cur = (h.currentPrice ?? '').trim();
	return cur !== '' && avg === cur ? 'seeded' : 'unknown';
}

// Back-fill holdings persisted before draft/link existed. Every pre-V7 holding is
// a real, active, unlinked position; assetClass/priceHistory back-fill from V5.
// Single place the holding shape is normalized so buildInitialState and the fetch
// merge agree.
export function migrateHolding(
	h: Partial<StockHolding> & { id: string },
): StockHolding {
	return {
		id: h.id,
		symbol: h.symbol ?? '',
		companyName: h.companyName ?? '',
		shares: h.shares ?? '',
		avgPrice: h.avgPrice ?? '',
		monthlyContribution: h.monthlyContribution ?? '',
		growthRate: h.growthRate ?? '',
		growthRateSource: h.growthRateSource ?? 'none',
		currentPrice: h.currentPrice ?? '',
		priceUpdatedAt: h.priceUpdatedAt ?? null,
		assetClass: h.assetClass ?? 'single-stock',
		priceHistory: h.priceHistory ?? [],
		// V13 — manualCurrentValue rides through UNTOUCHED and is never
		// populated from avgPrice or cost basis. Existing users' totals may
		// move when market value replaces cost basis, and gain lines may
		// disappear until average price is confirmed. Both are the fix working.
		manualCurrentValue: h.manualCurrentValue,
		manualValueUpdatedAt: h.manualValueUpdatedAt,
		avgPriceSource: migrateAvgPriceSource(h),
		targetAllocationPct: h.targetAllocationPct,
		annualYield: h.annualYield,
		reinvestDividends: h.reinvestDividends,
		isEmployer: h.isEmployer,
		linkedHoldingId: h.linkedHoldingId,
		status: h.status ?? 'active',
		awaitingPosition: h.awaitingPosition ?? false,
		reviewedAssumptions: h.reviewedAssumptions,
	};
}

// Resolve the effective inflation a payload should carry. V10 rule: a blank saved
// under V10+ is a deliberate off and stays; a blank from a pre-V10 payload is
// backfilled to the 3% default.
export function resolveInflationRate(
	stored: Partial<FinancialState>,
): string {
	const storedVersion = stored._schemaVersion ?? 0;
	if (storedVersion >= 10) {
		return (
			stored.assumptions?.inflationRate ??
			DEFAULT_ASSUMPTIONS.inflationRate
		);
	}
	return stored.assumptions?.inflationRate?.trim()
		? stored.assumptions.inflationRate
		: '3';
}

// V11 (Model B): pre-V11 returnRate was documented as real ("after inflation").
// nominal = real + inflation converts stored values without changing intent.
// Blank inflation adds 0, so inflation-off profiles keep their exact behavior.
export function migrateReturnRate(
	stored: Partial<FinancialState>,
	effInflationStr: string,
): string {
	const storedVersion = stored._schemaVersion ?? 0;
	const storedRR = stored.assumptions?.returnRate;
	if (storedVersion >= 11) {
		return storedRR ?? DEFAULT_ASSUMPTIONS.returnRate;
	}
	const real = parseFloat(storedRR ?? '7');
	const infl = parseFloat(effInflationStr);
	const nominal =
		(Number.isFinite(real) ? real : 7) + (Number.isFinite(infl) ? infl : 0);
	return String(Math.round(nominal * 10) / 10);
}

// incomeGrowthRate was a dead lever before V11; reset it to the neutral default
// so nobody inherits a silently pessimistic or optimistic wage curve.
export function migrateIncomeGrowthRate(
	stored: Partial<FinancialState>,
): string {
	const storedVersion = stored._schemaVersion ?? 0;
	return storedVersion >= 11
		? (stored.assumptions?.incomeGrowthRate ??
				DEFAULT_ASSUMPTIONS.incomeGrowthRate)
		: DEFAULT_ASSUMPTIONS.incomeGrowthRate;
}

// Merge stored state forward, back-filling newly-added fields so older
// localStorage payloads don't crash the reducers that read them.
export function buildInitialState(): FinancialState {
	const stored = loadFromStorage();
	if (!stored) return DEFAULT_STATE;

	const effInflationStr = resolveInflationRate(stored);
	const migratedReturnRate = migrateReturnRate(stored, effInflationStr);
	const migratedIncomeGrowthRate = migrateIncomeGrowthRate(stored);

	return {
		...DEFAULT_STATE,
		...stored,
		incomeEntries: (
			stored.incomeEntries ?? DEFAULT_STATE.incomeEntries
		).map((e) =>
			migrateIncomeEntry(e as Partial<IncomeEntry> & { id: string }),
		),
		expenseEntries: (
			stored.expenseEntries ?? DEFAULT_STATE.expenseEntries
		).map((e) =>
			migrateExpenseEntry(e as Partial<ExpenseEntry> & { id: string }),
		),
		surplusAllocations: stored.surplusAllocations ?? [],
		holdings: (stored.holdings ?? DEFAULT_STATE.holdings).map((h) =>
			migrateHolding(h as Partial<StockHolding> & { id: string }),
		),
		investedSavings:
			stored.investedSavings ?? DEFAULT_STATE.investedSavings,
		currentRunwaySaved:
			stored.currentRunwaySaved ?? DEFAULT_STATE.currentRunwaySaved,
		equityGrants: stored.equityGrants ?? [],
		lifestyleAssets: stored.lifestyleAssets ?? [],
		assumptions: {
			...DEFAULT_ASSUMPTIONS,
			...(stored.assumptions ?? {}),
			inflationRate: effInflationStr,
			returnRate: migratedReturnRate,
			incomeGrowthRate: migratedIncomeGrowthRate,
			returnOverrides: stored.assumptions?.returnOverrides ?? [],
			portfolioMode:
				stored.assumptions?.portfolioMode ??
				DEFAULT_ASSUMPTIONS.portfolioMode,
			// Existing users predate income mode → treat as after-tax so their
			// surplus/savings-rate/FI numbers don't shift underneath them.
			incomeMode:
				stored.assumptions?.incomeMode ??
				DEFAULT_ASSUMPTIONS.incomeMode,
			estimatedTaxRate:
				stored.assumptions?.estimatedTaxRate ??
				DEFAULT_ASSUMPTIONS.estimatedTaxRate,
		},
		_schemaVersion: SCHEMA_VERSION,
		_syncStatus: 'idle',
	};
}
